/**
 * CFMLabel - Component for CFM and other HVAC labels
 * Supports CFM labels, duct size labels, and equipment labels
 */

import React, { useState, useRef, useEffect } from 'react';
import { LABEL_TYPES } from '../../constants/hvacStamps';

/**
 * Label selector/placer panel
 */
export default function LabelPlacer({
  labelType = 'CFM',
  onSelectLabel,
  onClose,
  isMobile = false,
}) {
  const [value, setValue] = useState(LABEL_TYPES[labelType]?.defaultValue || '');
  const [showArrow, setShowArrow] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const labelDef = LABEL_TYPES[labelType];

  const handlePlace = () => {
    if (!value && labelType !== 'ROOM_LOAD') return;
    onSelectLabel({
      type: labelType.toLowerCase(),
      value: value,
      showArrow: showArrow,
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? 0 : '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#1a2332',
          borderRadius: isMobile ? '12px 12px 0 0' : '12px',
          width: isMobile ? '100%' : '320px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,200,255,0.05)',
        }}>
          <span style={{ color: '#00c8ff', fontWeight: '600', fontSize: '14px' }}>
            {labelType === 'CFM' ? 'CFM Label' :
             labelType === 'DUCT_SIZE' ? 'Duct Size Label' :
             labelType === 'EQUIPMENT' ? 'Equipment Label' : 'Label'}
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,100,100,0.15)',
              border: 'none',
              color: '#ff6666',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px' }}>
          {/* Value input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              color: '#8090a0',
              fontSize: '11px',
              display: 'block',
              marginBottom: '6px',
            }}>
              {labelType === 'CFM' ? 'CFM Value' :
               labelType === 'DUCT_SIZE' ? 'Duct Size (e.g., 6, 8, 10x8)' :
               labelType === 'EQUIPMENT' ? 'Equipment Label' : 'Value'}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                ref={inputRef}
                type={labelType === 'CFM' ? 'number' : 'text'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={labelType === 'CFM' ? '150' :
                            labelType === 'DUCT_SIZE' ? '8"' :
                            labelType === 'EQUIPMENT' ? '80K BTU Furnace' : ''}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: '#0d1117',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handlePlace();
                }}
              />
              {labelDef?.suffix && (
                <span style={{ color: labelDef.color, fontWeight: '600', fontSize: '14px' }}>
                  {labelDef.suffix}
                </span>
              )}
            </div>
          </div>

          {/* Arrow option for CFM labels */}
          {labelType === 'CFM' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={showArrow}
                  onChange={(e) => setShowArrow(e.target.checked)}
                  style={{ accentColor: '#00c8ff' }}
                />
                <span style={{ color: '#d0d8e0', fontSize: '12px' }}>
                  Show arrow pointer
                </span>
              </label>
              <p style={{ color: '#6080a0', fontSize: '10px', marginTop: '4px', marginLeft: '24px' }}>
                After placing, drag the arrow tip to point at a vent
              </p>
            </div>
          )}

          {/* Preview */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              color: '#8090a0',
              fontSize: '11px',
              display: 'block',
              marginBottom: '6px',
            }}>
              Preview
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              background: 'rgba(0,0,0,0.3)',
              borderRadius: '8px',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '6px 12px',
                background: 'rgba(0,0,0,0.6)',
                border: `2px solid ${labelDef?.color || '#00c8ff'}`,
                borderRadius: '4px',
              }}>
                <span style={{
                  color: labelDef?.color || '#00c8ff',
                  fontSize: '14px',
                  fontWeight: '700',
                }}>
                  {value || '---'} {labelDef?.suffix || ''}
                </span>
              </div>
              {showArrow && (
                <div style={{
                  width: '30px',
                  height: '2px',
                  background: labelDef?.color || '#00c8ff',
                  marginLeft: '-2px',
                }}>
                  <div style={{
                    position: 'relative',
                    left: '24px',
                    top: '-4px',
                    width: 0,
                    height: 0,
                    borderTop: '5px solid transparent',
                    borderBottom: '5px solid transparent',
                    borderLeft: `8px solid ${labelDef?.color || '#00c8ff'}`,
                  }} />
                </div>
              )}
            </div>
          </div>

          {/* Quick values for CFM */}
          {labelType === 'CFM' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                color: '#8090a0',
                fontSize: '11px',
                display: 'block',
                marginBottom: '6px',
              }}>
                Quick Values
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {[50, 75, 100, 125, 150, 200, 250, 300, 400].map((cfm) => (
                  <button
                    key={cfm}
                    onClick={() => setValue(cfm)}
                    style={{
                      padding: '6px 10px',
                      background: value === cfm ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
                      border: value === cfm ? '1px solid rgba(0,200,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      color: value === cfm ? '#00c8ff' : '#8090a0',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    {cfm}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick values for duct size */}
          {labelType === 'DUCT_SIZE' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                color: '#8090a0',
                fontSize: '11px',
                display: 'block',
                marginBottom: '6px',
              }}>
                Common Sizes
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {['4', '5', '6', '7', '8', '10', '12', '14', '8x8', '10x8', '12x8', '14x8'].map((size) => (
                  <button
                    key={size}
                    onClick={() => setValue(size)}
                    style={{
                      padding: '6px 10px',
                      background: value === size ? 'rgba(230,81,0,0.2)' : 'rgba(255,255,255,0.05)',
                      border: value === size ? '1px solid rgba(230,81,0,0.4)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '4px',
                      color: value === size ? '#e65100' : '#8090a0',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    {size}"
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Place button */}
          <button
            onClick={handlePlace}
            disabled={!value && labelType !== 'ROOM_LOAD'}
            style={{
              width: '100%',
              padding: '12px',
              background: value ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(0,200,255,0.4)',
              borderRadius: '8px',
              color: value ? '#00c8ff' : '#6080a0',
              fontSize: '13px',
              fontWeight: '600',
              cursor: value ? 'pointer' : 'not-allowed',
            }}
          >
            Click to Place Label
          </button>

          <p style={{
            color: '#6080a0',
            fontSize: '10px',
            textAlign: 'center',
            marginTop: '8px',
          }}>
            After clicking, click on the floor plan to place the label
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Label editor - appears when a placed label is selected
 * Non-blocking panel that allows canvas interaction for dragging
 */
export function LabelEditor({
  label,
  onUpdate,
  onDelete,
  onClose,
  isMobile = false,
}) {
  const [value, setValue] = useState(label.value);
  const labelDef = LABEL_TYPES[label.type?.toUpperCase()] || LABEL_TYPES.CFM;

  const handleSave = () => {
    onUpdate({ ...label, value });
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: isMobile ? 0 : '20px',
        right: isMobile ? 0 : '20px',
        left: isMobile ? 0 : 'auto',
        backgroundColor: '#1a2332',
        borderRadius: isMobile ? '12px 12px 0 0' : '12px',
        width: isMobile ? '100%' : '240px',
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        zIndex: 1000,
        padding: '12px',
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
      }}>
        <span style={{ color: '#00c8ff', fontSize: '11px', fontWeight: '600' }}>
          Edit Label
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,100,100,0.15)',
            border: 'none',
            color: '#ff6666',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '2px 6px',
            borderRadius: '4px',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Drag hint */}
      <p style={{
        color: '#6080a0',
        fontSize: '10px',
        marginBottom: '10px',
        textAlign: 'center',
      }}>
        Drag label on canvas to move
      </p>

      {/* Value input */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            type={label.type === 'cfm' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{
              flex: 1,
              padding: '8px 10px',
              background: '#0d1117',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '13px',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
          />
          {labelDef.suffix && (
            <span style={{ color: labelDef.color, fontWeight: '600', fontSize: '12px' }}>
              {labelDef.suffix}
            </span>
          )}
        </div>
      </div>

      {/* Arrow toggle for CFM labels */}
      {label.type === 'cfm' && (
        <div style={{ marginBottom: '10px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={label.showArrow}
              onChange={(e) => onUpdate({ ...label, showArrow: e.target.checked })}
              style={{ accentColor: '#00c8ff' }}
            />
            <span style={{ color: '#d0d8e0', fontSize: '11px' }}>
              Show arrow
            </span>
          </label>
        </div>
      )}

      {/* Save button */}
      <button
        onClick={handleSave}
        style={{
          width: '100%',
          padding: '8px',
          background: 'rgba(0,200,255,0.15)',
          border: '1px solid rgba(0,200,255,0.3)',
          borderRadius: '6px',
          color: '#00c8ff',
          fontSize: '11px',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '8px',
        }}
      >
        Save Changes
      </button>

      {/* Delete button */}
      <button
        onClick={onDelete}
        style={{
          width: '100%',
          padding: '8px',
          background: 'rgba(244, 67, 54, 0.15)',
          border: '1px solid rgba(244, 67, 54, 0.3)',
          borderRadius: '6px',
          color: '#f44336',
          fontSize: '11px',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Delete Label
      </button>
    </div>
  );
}
